import { useAddTrayContext } from '../AddTray.context'
import { useTrayStepContext } from '../AddTray.logic'
import { useUser } from 'app/User'
import { useGetTrayItemByBarcode } from 'lib/apollo/hooks'
import { useState, useCallback, useEffect } from 'react'
import { debounce } from 'lodash'

const useTrayIdLogic = () => {
  const { orgAbbreviation, orgName } = useUser()
  const { nextStep } = useTrayStepContext()
  const { trayBarcode, setTrayBarcode, showScanner, setShowScanner } =
    useAddTrayContext()

  const [isValidating, setIsValidating] = useState(false)
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [validationError, setValidationError] = useState<string>('')

  const { getTrayItem } = useGetTrayItemByBarcode()

  const debouncedValidation = useCallback(
    debounce(async (barcode: string) => {
      if (!barcode || barcode.trim().length === 0) {
        setIsDuplicate(false)
        setValidationError('')
        setIsValidating(false)
        return
      }

      setIsValidating(true)
      setValidationError('')

      try {
        const { data } = await getTrayItem({
          variables: { barcode: barcode.trim() },
        })
        const existingTray = data?.getTrayItemByBarcode

        if (existingTray?.id) {
          setIsDuplicate(true)
          setValidationError('This Tray ID already exists in the system')
        } else {
          setIsDuplicate(false)
          setValidationError('')
        }
      } catch (error) {
        setIsDuplicate(false)
        setValidationError('')
      } finally {
        setIsValidating(false)
      }
    }, 500),
    [getTrayItem]
  )

  useEffect(() => {
    debouncedValidation(trayBarcode)

    return () => {
      debouncedValidation.cancel()
    }
  }, [trayBarcode, debouncedValidation])

  const handleTrayBarcodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string
  ) => {
    const newValue = typeof e === 'string' ? e : e.target.value
    setTrayBarcode(newValue)

    if (isDuplicate || validationError) {
      setIsDuplicate(false)
      setValidationError('')
    }
  }

  const handleShowScannerButtonClick = () => {
    setShowScanner((prev) => !prev)
  }

  const handleCortexScan = (code: string) => {
    setTrayBarcode(code)
    setShowScanner(false)
  }

  const handleNext = () => {
    if (trayBarcode.length > 0 && !isDuplicate && !isValidating) {
      nextStep()
    }
  }

  return {
    trayBarcode,
    handleTrayBarcodeChange,
    showScanner,
    handleShowScannerButtonClick,
    handleCortexScan,
    handleNext,
    orgPrefix: orgAbbreviation ?? orgName,
    isValidating,
    isDuplicate,
    validationError,
  }
}

export default useTrayIdLogic
